import {
	Extension,
	ExtensionProperty,
	type IProperty,
	type Nullable,
	PropertyType,
	type ReaderContext,
	type Texture,
	TextureInfo,
	type vec3,
	type WriterContext,
} from "@gltf-transform/core";
import {
	EquirectangularReflectionMapping,
	LoaderUtils,
	type Material as MaterialDef,
	type MeshStandardMaterial,
} from "three";
import type {
	GLTFLoaderPlugin,
	GLTFParser,
} from "three/addons/loaders/GLTFLoader.js";
import type { DoppleHDRLoader } from "./DoppleHDRLoader";

export const EXTENSION_NAME = "DPL_material_environment";

export interface IMaterialEnvironment extends IProperty {
	environmentTexture: Texture;
	environmentTextureInfo: TextureInfo;
	intensity: number;
	environmentRotation: vec3;
}

export class GLTFMaterialEnvironmentExtension implements GLTFLoaderPlugin {
	parser: GLTFParser;
	name: string;
	#doppleHDRLoader: DoppleHDRLoader;

	constructor(parser: GLTFParser, loader: DoppleHDRLoader) {
		this.parser = parser;
		this.name = EXTENSION_NAME;
		this.#doppleHDRLoader = loader;
	}

	async loadMaterial(materialIndex: number): Promise<MaterialDef> {
		const materialDef = this.parser.json.materials[materialIndex];

		if (!materialDef.extensions?.[this.name]) {
			return this.parser.loadMaterial(materialIndex);
		}

		const material = (await this.parser.loadMaterial(
			materialIndex,
		)) as MeshStandardMaterial;

		const { intensity, environmentRotation, environmentTexture } = materialDef
			.extensions[this.name] as Record<string, unknown> & {
			intensity: number;
			environmentRotation: number[];
			environmentTexture: { index: number };
		};

		const textureDef = this.parser.json.textures?.[environmentTexture.index];
		const imageDef = this.parser.json.images[textureDef.source as number];
		const imageUri = this.parser.options.manager.resolveURL(imageDef.uri);
		const encodingOptions = imageDef.extensions?.DPL_texture_environment || {
			gamma: 1.0,
		};
		const doppleHDRLoaderResult = await this.#doppleHDRLoader
			.loadWithMetaData(
				LoaderUtils.resolveURL(imageUri, this.parser.options.path),
				{
					gamma: encodingOptions.gamma,
				},
			)
			.catch((err) => {
				console.error(err);
				return null;
			});
		if (doppleHDRLoaderResult) {
			doppleHDRLoaderResult.mapping = EquirectangularReflectionMapping;
			doppleHDRLoaderResult.needsUpdate = true;
			material.envMap = doppleHDRLoaderResult;
		}

		material.envMapIntensity = intensity;
		material.envMapRotation.set(
			environmentRotation[0],
			environmentRotation[1],
			environmentRotation[2],
		);
		material.needsUpdate = true;

		return material;
	}
}

export class MaterialEnvironment extends ExtensionProperty<IMaterialEnvironment> {
	static EXTENSION_NAME = EXTENSION_NAME;
	extensionName = EXTENSION_NAME;
	propertyType = "MaterialEnvironment";
	parentTypes = [PropertyType.MATERIAL];

	protected init() {
		this.extensionName = EXTENSION_NAME;
		this.propertyType = "MaterialEnvironment";
		this.parentTypes = [PropertyType.MATERIAL];
	}

	protected getDefaults(): Nullable<IMaterialEnvironment> {
		return Object.assign(super.getDefaults() as IProperty, {
			environmentTexture: null,
			environmentTextureInfo: new TextureInfo(
				this.graph,
				"enviormentTextureInfo",
			),
			intensity: 1.0,
			environmentRotation: [0, 0, 0] as vec3,
		});
	}

	setEnvironmentTexture(environmentTexture: Texture | null) {
		return this.setRef("environmentTexture", environmentTexture);
	}

	getEnvironmentTexture(): Texture | null {
		return this.getRef("environmentTexture");
	}

	getEnvironmentTextureInfo() {
		return this.getRef("environmentTexture")
			? this.getRef("environmentTextureInfo")
			: null;
	}

	setIntensity(intensity: number) {
		return this.set("intensity", intensity);
	}

	getIntensity() {
		return this.get("intensity") ?? 1;
	}

	setEnvironmentRotation(rotation: [number, number, number]) {
		return this.set("environmentRotation", rotation) ?? [0, 0, 0];
	}

	getEnvironmentRotation() {
		return this.get("environmentRotation");
	}
}

export class DPLMaterialEnvironment extends Extension {
	static EXTENSION_NAME = EXTENSION_NAME;
	extensionName = EXTENSION_NAME;
	prereadTypes = [PropertyType.MATERIAL];
	prewriteTypes = [PropertyType.MATERIAL];

	createMaterialEnvironment() {
		return new MaterialEnvironment(this.document.getGraph());
	}

	read(_context: ReaderContext): this {
		return this;
	}

	write(context: WriterContext): this {
		for (const material of this.document.getRoot().listMaterials()) {
			const materialEnvironment =
				material.getExtension<MaterialEnvironment>(EXTENSION_NAME);

			if (materialEnvironment) {
				const materialIndex = context.materialIndexMap.get(material);
				if (materialIndex === undefined) {
					continue;
				}

				const materialDef = context.jsonDoc.json.materials?.[materialIndex];

				if (!materialDef) {
					continue;
				}

				materialDef.extensions ??= {};

				const texture = materialEnvironment.getEnvironmentTexture();
				const textureInfo = materialEnvironment.getEnvironmentTextureInfo();

				if (!(texture && textureInfo)) {
					continue;
				}

				materialDef.extensions[EXTENSION_NAME] = {
					environmentTexture: context.createTextureInfoDef(
						texture,
						textureInfo,
					),
					intensity: materialEnvironment.getIntensity(),
					environmentRotation: materialEnvironment.getEnvironmentRotation(),
				};
			}
		}

		return this;
	}
}

interface IEnvironment extends IProperty {
	gamma: number;
	brightness: number;
	contrast: number;
	exposure: number;
	saturation: number;
}

export class Environment extends ExtensionProperty<IEnvironment> {
	static EXTENSION_NAME = EXTENSION_NAME;
	extensionName = EXTENSION_NAME;
	propertyType = "TextureEnvironment";
	parentTypes = [PropertyType.TEXTURE];

	protected init() {
		this.extensionName = EXTENSION_NAME;
		this.propertyType = "MaterialEnvironment";
		this.parentTypes = [PropertyType.MATERIAL];
	}

	protected getDefaults(): Nullable<IEnvironment> {
		return Object.assign(super.getDefaults() as IProperty, {
			gamma: 1.0,
			brightness: 1.0,
			contrast: 1.0,
			exposure: 1.0,
			saturation: 1.0,
		});
	}

	setGamma(gamma: number) {
		return this.set("gamma", gamma);
	}

	getGamma() {
		return this.get("gamma");
	}

	setBrightness(brightness: number) {
		return this.set("brightness", brightness);
	}

	getBrightness() {
		return this.get("brightness");
	}

	setContrast(contrast: number) {
		return this.set("contrast", contrast);
	}

	getContrast() {
		return this.get("contrast");
	}

	setExposure(exposure: number) {
		return this.set("exposure", exposure);
	}

	getExposure() {
		return this.get("exposure");
	}

	setSaturation(saturation: number) {
		return this.set("saturation", saturation);
	}

	getSaturation() {
		return this.get("saturation");
	}
}

const EXT_NAME = "DPL_texture_environment";

export class DPLTextureEnvironment extends Extension {
	public readonly extensionName = EXT_NAME;
	/** @hidden */
	public readonly prereadTypes = [PropertyType.TEXTURE];
	public static readonly EXTENSION_NAME = EXT_NAME;

	createEnvironment() {
		return new Environment(this.document.getGraph());
	}

	/** @hidden */
	public preread(context: ReaderContext): this {
		const textureDefs = context.jsonDoc.json.textures || [];

		for (const textureDef of textureDefs) {
			if (textureDef.extensions?.[EXT_NAME]) {
				textureDef.source = (
					textureDef.extensions[EXT_NAME] as { source: number }
				).source;
			}
		}

		return this;
	}

	/** @hidden */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public read(context: ReaderContext): this {
		for (const [
			imageIndex,
			imageDef,
		] of context.jsonDoc.json.images?.entries() || []) {
			const extDef = imageDef.extensions?.[EXTENSION_NAME] as IEnvironment;

			if (!extDef) {
				continue;
			}

			const environment = this.createEnvironment();

			environment.setGamma(extDef.gamma);
			environment.setBrightness(extDef.brightness);
			environment.setContrast(extDef.contrast);
			environment.setExposure(extDef.exposure);
			environment.setSaturation(extDef.saturation);

			context.textures[imageIndex].setExtension(EXTENSION_NAME, environment);
		}

		return this;
	}

	/** @hidden */
	public write(context: WriterContext): this {
		const jsonDoc = context.jsonDoc;

		for (const texture of this.document.getRoot().listTextures()) {
			const imageIndex = context.imageIndexMap.get(texture);

			if (imageIndex === undefined) {
				continue;
			}

			const imageDef = jsonDoc.json.images?.[imageIndex];

			if (!imageDef) {
				continue;
			}

			const ext = texture.getExtension<Environment>(EXT_NAME);

			if (!ext) {
				continue;
			}

			imageDef.extensions ??= {};

			imageDef.extensions[EXT_NAME] = {
				gamma: ext?.getGamma(),
				brightness: ext?.getBrightness(),
				contrast: ext?.getContrast(),
				exposure: ext?.getExposure(),
				saturation: ext?.getSaturation(),
			};
		}

		return this;
	}
}
